import { injectable, inject } from '@patsnap/tahiti-core/shared/inversify';
import type { ParagraphHighlightService, ParagraphHighlightContextMenuItem } from './paragraph-highlight-service.interface';
import ParagraphWording from '@/lib/paragraph-highlight/index';
import { LanguageService } from '@analytics/shared/services/language-service';
import { HIRO_EVENT_PROXY, type IEventProxy } from '@patsnap/hiro-event-proxy';

declare module '@/lib/paragraph-highlight/index' {
    export class ParagraphWording {
        static updateDynamicMenu(menu: {
            addPopover: ParagraphHighlightContextMenuItem[],
            delPopover: ParagraphHighlightContextMenuItem[],
        }, isShow: boolean): void;
    }
}



@injectable()
export class ParagraphHighlightServiceImpl implements ParagraphHighlightService {
    protected readonly summaryMessages = {
        en: 'Summary',
        cn: '总结',
        tw: '總結',
        jp: '要約',
    };

    protected readonly translateMessages = {
        en: 'Translate',
        cn: '翻译',
        tw: '翻譯',
        jp: '翻訳',
    };

    constructor(
        @inject(LanguageService)
        private readonly _languageService: LanguageService,
        @inject(HIRO_EVENT_PROXY)
        private readonly _hiroEventProxy: IEventProxy,
    ) {
    }

    get locale() {
        return this._languageService.getCurrentLanguage();
    }

    get summaryLabel() {
        return this.summaryMessages[this.locale];
    }

    get translateLabel() {
        return this.translateMessages[this.locale];
    }

    get addPopover():  ParagraphHighlightContextMenuItem[] {
        const self = this;

        return [
            {
                key: 'summary',
                icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"><path fill="#fff" fill-opacity=".01" d="M0 0h24v24H0z"/><path d="M4 13v-2a1 1 0 01-1-1V9a1 1 0 011-1V6a2 2 0 012-2h11a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2v-2a1 1 0 01-1-1v-1a1 1 0 011-1zm2 0a1 1 0 011 1v1a1 1 0 01-1 1v1a1 1 0 102 0V7a1 1 0 00-2 0v1a1 1 0 011 1v1a1 1 0 01-1 1v2z" fill="#495973"/><path d="M4 13v-2a1 1 0 01-1-1V9a1 1 0 011-1V6a2 2 0 012-2h11a2 2 0 012 2v12a2 2 0 01-2 2H6a2 2 0 01-2-2v-2a1 1 0 01-1-1v-1a1 1 0 011-1zm2 0a1 1 0 011 1v1a1 1 0 01-1 1v1a1 1 0 102 0V7a1 1 0 00-2 0v1a1 1 0 011 1v1a1 1 0 01-1 1v2z" fill="url(#prefix__paint0_linear_520_2686)"/><defs><linearGradient id="prefix__paint0_linear_520_2686" x1="3" y1="4" x2="3" y2="20" gradientUnits="userSpaceOnUse"><stop stop-color="#18EC81"/><stop offset=".424" stop-color="#0DACBA"/><stop offset="1" stop-color="#0A3DFF"/></linearGradient></defs></svg>',
                label: self.summaryLabel,
                callback(text: string) {
                    self._summary(text);
                },
            },
            {
                key: 'translate',
                icon: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" fill="none"><path d="M10 12h1.5a.5.5 0 00.5-.5v-1a.5.5 0 00-.5-.5H10V9h2.5a.5.5 0 00.5-.5v-1a.5.5 0 00-.5-.5h-4a.5.5 0 00-.5.5v7a.5.5 0 00.5.5h4a.5.5 0 00.5-.5v-1a.5.5 0 00-.5-.5H10v-1zm6.846-5h1.823a2 2 0 011.977 2.304l-1.385 9A2 2 0 0117.284 20H11a2 2 0 01-2-2H6a2 2 0 01-2-2V6a2 2 0 012-2h8.963a2 2 0 011.982 2.267L16.846 7zm-.269 2l-.978 7.267A2 2 0 0113.617 18H11h6.284l1.385-9h-2.092z" fill="#495973"/><path d="M10 12h1.5a.5.5 0 00.5-.5v-1a.5.5 0 00-.5-.5H10V9h2.5a.5.5 0 00.5-.5v-1a.5.5 0 00-.5-.5h-4a.5.5 0 00-.5.5v7a.5.5 0 00.5.5h4a.5.5 0 00.5-.5v-1a.5.5 0 00-.5-.5H10v-1zm6.846-5h1.823a2 2 0 011.977 2.304l-1.385 9A2 2 0 0117.284 20H11a2 2 0 01-2-2H6a2 2 0 01-2-2V6a2 2 0 012-2h8.963a2 2 0 011.982 2.267L16.846 7zm-.269 2l-.978 7.267A2 2 0 0113.617 18H11h6.284l1.385-9h-2.092z" fill="url(#prefix__paint0_linear_520_2690)"/><defs><linearGradient id="prefix__paint0_linear_520_2690" x1="4" y1="4" x2="4" y2="20" gradientUnits="userSpaceOnUse"><stop stop-color="#18EC81"/><stop offset=".424" stop-color="#0DACBA"/><stop offset="1" stop-color="#0A3DFF"/></linearGradient></defs></svg>',
                label: self.translateLabel,
                callback(text: string) {
                    self._translate(text);
                },
            },
        ];
    }

    get delPopover(): ParagraphHighlightContextMenuItem[] {
        return this.addPopover;
    }

    private _summary(text: string) {
        this._hiroEventProxy.emitSummary({
            text,
        });
    }

    private _translate(text: string) {
        this._hiroEventProxy.emitTranslate({
            text,
        });
    }

    updateContextMenu() {
        ParagraphWording.updateDynamicMenu({
            addPopover: this.addPopover,
            delPopover: this.delPopover,
        }, false);
    }
}
